/**
 * Scanners identify types of tokens/values in the abstract text
 * @module
 */
import * as _ from 'lodash'
//import * as P from 'parsimmon'
import { Annotation, findSection, findSentence, TypedRange } from './Annotations'
import { SectionLabel } from './abstract-structure'
import QuantifierParser, { DEV_SEP } from 'util/QuantifierParser'
import { validateQuantifier } from 'util/quantifier'
import { fetchAliases, fetchUnits, validDosageUnit, validTimeUnit } from './units'
import { UnitOfMeasure } from 'types/dtos'
import { Quantifier } from 'types/bindings'

/**
 * Produces annotations, no "visual" ones overlapping
 */
export abstract class Scanner {
	readonly type: string
	readonly interactive?: boolean
	readonly sections?: SectionLabel[]
	abstract scan(context: TypedRange, source?: string): Annotation[]
	match(text: string, source?: string) {
		text = text || ''
		const a = this.scan({ start: 0, end: text.length - 1, text })
		return (a.length == 1 && [text.slice(0, a[0].start), text.slice(a[0].end + 1)].every(s => s.trim().length == 0)) && a[0]
	}
}

export class RegExpScanner extends Scanner {
	readonly interactive?: boolean
	constructor(readonly type: string,
	            private readonly re: RegExp,
	            readonly entireMatch = 0) {
		super()
		if (!re.flags.includes('g')) throw new Error('Scanning requires global matching')
	}
	scan(context: TypedRange, source?: string): Annotation[] {
		const re = this.re
		re.lastIndex = 0

		const annotations = []
		let match
		while (match = re.exec(context.text)) {
			const annot = this.createAnnotation(context, source, re, match)
			if (annot) annotations.push(annot)
		}
		return annotations
	}

	protected createAnnotation(context, source, re, match) {
		const type = this.type
		let start = match.index
		let i = 1
		for (; i < this.entireMatch; i++) {
			start += match[i].length
		}
		const end = start + match[this.entireMatch].length - 1
		const txt = context.text.slice(start, end + 1)
		return Object.assign({
			start: context.start + start,
			end: context.start + end,
			text: txt,
			type,
			types: [type],
			source,
			section: findSection(context),
			sentence: findSentence(context),
			captured: match[this.entireMatch + 1] || txt,
			parsed: match[this.entireMatch + 2]
		})
	}
}

class WrappingScanner extends Scanner {
	constructor(protected scanner: Scanner) {
		super()
	}
	match(text: string, source?: string) {
		return this.scanner.match(text, source)
	}
	scan(context: TypedRange, source?: string): Annotation[] {
		return this.scanner.scan(context, source)
	}
}

class LinkScanner extends WrappingScanner {
	constructor(protected scanner: Scanner, protected readonly baseUrl: string, protected readonly convertId = i => i) {
		super(scanner)
	}
	match(text: string, source?: string) {
		return this.scanner.match(text, source)
	}
	scan(context: TypedRange, source?: string): Annotation[] {
		return (this.scanner.scan(context, source) || []).map(annot => this.addUrl(annot))
	}
	protected addUrl(annot) {
		annot.url = this.baseUrl + this.convertId(annot.captured)
		return annot
	}
}

const NCTID = /\bNCT\s?\d{8}\b/ig
class NCTIdScanner extends RegExpScanner {
	constructor() {
		super('nct-id', NCTID)
	}
}

export const NCTIdLinkScanner = new LinkScanner(new NCTIdScanner(), 'https://clinicaltrials.gov/ct2/show/', id => id.replace(/\s/g, ''))

export class LabelScanner extends RegExpScanner {
	readonly interactive = false
	constructor() {
		super('label', /\b(([A-Z]{4,}(?:(?:(?:,? )|\/)?[A-Z]{3,})*(?=\s?:))|(Background|Leading|Methods and Results|Methods|Objectives?|Results|Conclusions|Unique identifier|Clinical Trial Registration URL|CONTEXT|OBJECTIVES?|DESIGN, SETTING, AND PATIENTS|INTERVENTION|MAIN OUTCOME MEASURES|RESULTS|CONCLUSION|TRIAL REGISTRATION)):?/g)
	}

	protected createAnnotation(context, source, re, match): any {
		const a = super.createAnnotation(context, source, re, match)
		// we either capture one form or the other
		a.captured = match[1] || a.text
		return a
	}
}

function parseQuantity(str) {
	let ispct = false
	let type
	str = str.replace(/\xB7/g, '.')
	const types = []
	if (str.endsWith('%')) {
		ispct = true
		types.push(type = 'pct')
		str = str.slice(0, str.length - 1)
	} else {
		types.push(type = 'num')
	}
	const num = Number(str.replace(/,/ig, ''))
	const isneg = num < 0
	const prefix = isneg ? 'neg' : 'pos'
	types.push(prefix + '-' + type)
	const isint = !ispct && Number.isInteger(num)
	if (isint) types.push(prefix + '-int')
	// if (!isneg) {
	// 	if (isint || ispct) {
	// 		annot.types.push('binary-value')
	// 	}
	// 	if (isint) {
	// 		annot.types.push('group-n')
	// 	}
	// }
	return {
		parsed: num,
		ispct,
		isneg,
		isint,
		types
	}
}

export class QuantityScanner extends RegExpScanner {
	constructor(type = 'quantity') {
		super(type, new RegExp(b(`(${RE_NUMBER}%?)`), 'ig'), 2)
	}

	protected createAnnotation(context, source, re, match): any {
		const annot = super.createAnnotation(context, source, re, match)
		const { parsed, types } = parseQuantity(annot.captured)
		annot.parsed = parsed
		annot.types.push(...types)
		return annot
	}
}

export class BinaryValueScanner extends QuantityScanner {
	constructor() {
		super('binary-value')
	}

	protected createAnnotation(context, source, re, match): any {
		const annot = super.createAnnotation(context, source, re, match)
		// only allow positive percents or integer-only non-percents
		if (annot.types.includes('pos-pct') || annot.types.includes('pos-int')) {
			return annot
		}
	}
}

export class CountValueScanner extends QuantityScanner {
	constructor() {
		super('count-value')
	}

	protected createAnnotation(context, source, re, match): any {
		const annot = super.createAnnotation(context, source, re, match)
		// only allow positive integers
		if (annot.types.includes('pos-int')) {
			return annot
		}
	}
}

export class GroupNScanner extends RegExpScanner {
	constructor() {
		super('group-n', new RegExp(`\\b(n\\s*=\\s*(${RE_POS_NUMBER}%?))`, 'ig'), 1)
	}
	protected createAnnotation(context, source, re, match): any {
		const captured = match[this.entireMatch + 1]
		if (captured[captured.length - 1] != '%') {
			const parsed = Number(captured)
			if (Number.isInteger(parsed)) {
				const annot = super.createAnnotation(context, source, re, match)
				annot.parsed = parsed
				return annot
			}
		}
	}
}

const EQ_OPS = ['~=', '~>', '~>=', '~<', '~<=', '=', '>', '>=', '≥', '<', '<=', '≤', '!=']
const EQ_OPS_RE = '(?:' + EQ_OPS.join('|') + ')'


let initialized
export function initializeQuantifierParser() {
	return initialized || (initialized = Promise.all([fetchUnits(), fetchAliases()]).then(([uoms, aliases]) => {
		QuantifierParser.init(_.transform(EQ_OPS, (dict, str) => dict[str] = true, {}), uoms, aliases)
		initialized = Promise.resolve(true)
	}))
}

type UnitValidator = (unit: UnitOfMeasure) => boolean
interface QSOpts {
	allowSD?: boolean
	requireValidUnits?: boolean
	acceptUnit?: UnitValidator
}

const ANY_UNIT = /.+/

const UNIT_HAS_NAME = u => u.Name.Name?.length

export function classifyQuantifier(q: Quantifier) {
	const types = []
	const isUnitLess = u => !u || ['na', 'n/a', ''].includes((u.Name?.Name || '').toLowerCase())
	if (!q.Low) {
		// a single value
		if (q.High.Units.length == 1) {
			const value = q.High.Units[0]
			if (!value.EqualityOperator || value.EqualityOperator == '=') {
				const unitless = isUnitLess(value.Unit)
				const positive = value.Number >= 0
				if (positive) {
					if (unitless) {
						// scalar positive number
						types.push('binary-value')
						types.push('pos-num')
						if (Number.isInteger(value.Number)) {
							types.push('pos-int')
							if (value.Number > 0) {
								types.push('group-n')
							}
						}
					} else if (value.Unit) {
						if (value.Unit.Name?.Name == '%') {
							types.push('binary-value')
						}
					}
				} else if (unitless) {
					types.push('neg-num')
					if (Number.isInteger(value.Number)) {
						types.push('neg-int')
					}
				}
			}
		}
	}
	const units = [...(q.Low?.Units || []), ...(q.High?.Units || [])]
	const timepoint = _.every(units, u => validTimeUnit(u.Unit))
	if (timepoint) {
		types.push('timepoint')
	} else if (_.some(units, u => validDosageUnit(u.Unit))) {
		types.push('dosage')
	}
	return types
}

export class QuantifierScanner extends Scanner {
	readonly interactive = true
	readonly allowSD: boolean = true
	readonly requireValidUnits: boolean = true
	readonly acceptUnit = UNIT_HAS_NAME
	constructor(readonly type = 'measure', opts: QSOpts = {}) {
		super()
		if ('allowSD' in opts) this.allowSD = opts.allowSD
		if ('requireValidUnits' in opts) this.requireValidUnits = opts.requireValidUnits
		if ('acceptUnit' in opts) this.acceptUnit = opts.acceptUnit
	}
	scan(context: TypedRange, source?: string, filter?: (start, end, text) => boolean): Annotation[] {
		let result = QuantifierParser.parseText(context.text, false)
		if (!this.allowSD || this.requireValidUnits || this.acceptUnit) {
			result = result.filter(result => validateQuantifier(result.quantifier, this.allowSD, this.requireValidUnits, this.acceptUnit))
		}
		const accepted = []
		for (const r of result) {
			const classes = this.classifyQuantifier(r.quantifier)
			// don't accept unclassified quantifiers
			if (!classes.length) continue
			accepted.push({
				type: this.type,
				types: _.uniq([this.type, ...classes]),
				start: context.start + r.offset,
				end: context.start + r.offset + r.length - 1,
				text: context.text.slice(r.offset, r.offset + r.length),
				source,
				captured: r.quantifier.Gestalt,
				parsed: r,
				//visual: false,
				section: findSection(context),
				sentence: findSentence(context)
			})
		}
		return accepted
	}

	protected classifyQuantifier(q: Quantifier) {
		return classifyQuantifier(q)
	}
}

class ContinuousValueScanner extends QuantifierScanner {
	constructor() {
		super('continuous-value', { allowSD: true })
	}
}

class TimepointScanner extends QuantifierScanner {
	constructor() {
		super('timepoint', {
			acceptUnit: u => UNIT_HAS_NAME(u) && validTimeUnit(u)
		})
	}
}

class DosageScanner extends QuantifierScanner {
	constructor() {
		super('dosage')
	}
	protected classifyQuantifier(q: Quantifier) {
		const classes = super.classifyQuantifier(q)
		return classes.includes('dosage') ? classes : []
	}
}

const SBRK = '(?:^|[\\s\\/\\\\(){};\\[])'
const EBRK = '(?=[\\s\\/\\\\(){}.,;\\]]|$)'
const INTEGER = `(?:\\d(?:,?\\d)*)`
const MIDDLE_DOT = '\xB7'
const FRACTIONAL = `(?:[\\.\\xB7]${INTEGER})`
const RE_POS_NUMBER = `(?:(?:${INTEGER}${FRACTIONAL}?)|${FRACTIONAL})`
const RE_NEG_NUMBER = '-' + RE_POS_NUMBER
const SD_RE = `${DEV_SEP}\\s*(${RE_POS_NUMBER})`
export const CTNS_RE = `(${RE_POS_NUMBER})\\s*(?:${SD_RE})?`
const RE_NUMBER = '-?' + RE_POS_NUMBER
const RE_HR_TEXT = '(?:(?:hazard\\s*ratio)|(?:\\[?HR\\]?))'
const RE_PCT = RE_POS_NUMBER + '%'
const RE_CONF_RANGE = `(${RE_NUMBER})\\s*(?:to|-)\\s*(${RE_NUMBER})`
const RE_CI_TEXT = '(?:(?:confidence\\sinterval)|(?:CI))'
const RE_CI = `((?:(?:${RE_CI_TEXT},?\\s+(${RE_POS_NUMBER})%)|(?:(${RE_POS_NUMBER})%,?\\s+${RE_CI_TEXT})),?\\s+${RE_CONF_RANGE})`
export const RE_FORMATTED_CI = `${RE_POS_NUMBER}% ${RE_NUMBER} - ${RE_NUMBER}`
const c = (txt, capture) => capture ? `(${txt})` : txt
//const b = s => '\\b' + s + '\\b'
const b = s => `(${SBRK})` + s + EBRK

// const WholeNumber = P.regexp(/\d(?:,?\d)*/).desc('whole number')
// const toWholeNumber = s => Number(s.replace(',', ''))
// const Fractional = P.seq(P.oneOf('.\xB7'), WholeNumber).tie()
// const Integer = P.seq(P.string('-').atMost(1).tie(), P.alt(P.seq(WholeNumber, Fractional.atMost(1).tie()), Fractional).tie()).tie().map(toWholeNumber)
// const PosNumber = Integer.chain(i => {
// 	return i >= 0 ? P.succeed(i) : P.fail('Must be positive')
// }).node('Positive Integer')
// const NegNumber = Integer.chain(i => {
// 	return i < 0 ? P.succeed(i) : P.fail('Must be positive')
// }).node('Negative Integer')
// const NValue = P.seqMap(P.regex(/n\s*=\s*/), WholeNumber.map(toWholeNumber).node('N Value'), (__, node) => node)
//
// const whole = WholeNumber.parse('1,234')
// const whole1 = WholeNumber.parse('-1,234')
// const whole2 = WholeNumber.parse('234.0')
// const int = Integer.parse('1,234.5')
// const int1 = Integer.parse('-1,234.5')
// const pos = PosNumber.parse('1,234.5')
// const pos2 = PosNumber.parse('-1,234.5')
// const neg = NegNumber.parse('1,234.5')
// const neg2 = NegNumber.parse('-1,234.5')
// const n = NValue.parse('n = 1,234')
// const n2 = NValue.parse('n = 1234.0')
// const n3 = NValue.parse('n = -1234')
//
// //const NValues = P.seq(P.any.many().notFollowedBy(NValue), NValue.atMost(1)).many()
// const NValues = NValue.sepBy(P.any)
// const test = NValues.parse('n = 1234')
// const test2 = NValues.parse(' asdfasdf  n=1234 asdfadf n = 21  asdfads n=5')


//export const CONF_INTVL1 = new RegExpScanner('ci', new RegExp(b(`(${RE_CI_TEXT},?\\s+(${RE_POS_NUMBER}%),?\\s+ ${RE_CONF_RANGE})`), 'ig'), 2)
//export const CONF_INTVL2 = new RegExpScanner('ci', new RegExp(b(`((${RE_POS_NUMBER}%),?\\s+${RE_CI_TEXT},?\\s+${RE_CONF_RANGE})`), 'ig'), 2)

export class CI extends RegExpScanner {
	constructor() {
		super('ci', new RegExp(b(RE_CI), 'ig'), 2)
	}

	protected createAnnotation(context, source, re, match): any {
		const annot = super.createAnnotation(context, source, re, match);
		const pct = (match[3] || match[4])
		annot.captured = pct + '% ' + match[5] + ' - ' + match[6]
		annot.parsed = [Number(pct), Number(match[5]), Number(match[6])]
		return annot
	}
}

export const LABEL = new LabelScanner()
export const SD = new RegExpScanner('sd', new RegExp(b(`(±\\s*${RE_POS_NUMBER})`), 'ig'), 2)
export const GROUPN = new GroupNScanner()
export const PVALUE = new RegExpScanner('p-value', new RegExp(b(`(p\\s*${EQ_OPS_RE}\\s*(${RE_POS_NUMBER}))`), 'ig'), 2)
export const AVALUE = new RegExpScanner('a-value', new RegExp(b(`([aα]\\s*${EQ_OPS_RE}\\s*(${RE_POS_NUMBER}))`), 'ig'), 2)
export const HZRD_RATIO = new RegExpScanner('hr', new RegExp(b(`(${RE_HR_TEXT},?\\s+(${RE_POS_NUMBER}))`), 'ig'), 2)
export const QUANTIFIER = new QuantifierScanner()
export const QUANTITY = new QuantityScanner() //new RegExpScanner('quantity', new RegExp(b(`(${RE_NUMBER})`), 'ig'), 2) ///
export const BINARY_VALUE = new BinaryValueScanner()
export const COUNT_VALUE = new CountValueScanner()
export const AGE_VALUE = new RegExpScanner('age', new RegExp(b(`(${CTNS_RE})`), 'ig'), 2)
export const CONT_VALUE = new ContinuousValueScanner()
export const TIMEPOINT = new TimepointScanner()
export const DOSAGE = new DosageScanner()